import { Settings } from '@skezi/ui';
import { FC, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { Seo } from '../../components/common/Seo';
import * as Icon from '../../components/icons';

interface DashboardLayoutProps {}

const DashboardLayout: FC<DashboardLayoutProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchQueryParams = useSearchParams()[0];
  const { user } = useAuth();

  const [isTabContentHeightShort, setIsTabContentHeightShort] = useState<boolean>(false);

  const activeTabClasses =
    'text-blue-primary font-semibold md:bg-[#FFF6EB] md:border-r-4 md:border-r-orange-primary max-md:border-b-4 max-md:border-b-orange-primary';

  const menuItems = [
    {
      name: 'Mes applications',
      link: '/dashboard/apps',
    },
    {
      name: 'Connexions API',
      link: '/dashboard/api-connections',
    },
  ];

  const getSettingsBreadcrumb = (pathname: string) => {
    let settingType: 'profile' | 'privacy' | 'subscription' | null;
    let title: string;
    switch (true) {
      case pathname.includes('email'):
      case pathname.includes('nickName'):
      case pathname.includes('password'):
        settingType = 'profile';
        title = 'Authentification';
        break;
      case pathname.includes('doubleAuthentication'):
      case pathname.includes('deleteAccount'):
        settingType = 'privacy';
        title = 'Sécurité';
        break;
      default:
        settingType = null;
        title = 'Paramètres du compte';
        break;
    }
    return {
      url: settingType ? `/dashboard/settings/${settingType}` : '/dashboard/settings',
      title,
    };
  };

  useEffect(() => {
    (async () => {
      const projectLogoSrc = searchQueryParams.get('projectLogoSrc');
      const projectName = searchQueryParams.get('projectName');
      localStorage.setItem('projectLogoSrc', projectLogoSrc ? projectLogoSrc : '');
      localStorage.setItem('projectName', projectName ? projectName : '');

      if (window.innerHeight > document.documentElement.offsetHeight) {
        setIsTabContentHeightShort(true);
      } else {
        setIsTabContentHeightShort(false);
      }

      const mailUpdatedToken = searchQueryParams.get('mailUpdatedToken');
      if (mailUpdatedToken) {
        navigate(`/dashboard/settings/profile`);
      }
    })();
  }, []);

  return (
    <>
      <div>
        <div>
          <div className='my-5 flex items-center max-md:justify-center md:h-7.5 md:justify-between'>
            <span className='max-md:ml-0 md:ml-10'>
              <Icon.Skezi size={1.5} />
            </span>
            <span className='mr-6 flex max-md:hidden'>
              <span className='text-gray-primary text-[0.75rem]'>{user?.email}</span>
            </span>
            <span
              className='absolute right-0 mr-5 flex cursor-pointer items-center hover:font-medium hover:underline md:hidden'
              onClick={() => {
                navigate(`/logout`);
              }}
            >
              <Icon.Logout className='' />
            </span>
            <span
              className='absolute right-8 mr-5 flex cursor-pointer items-center hover:font-medium hover:underline md:hidden'
              onClick={() => {
                navigate(`/dashboard/settings`);
              }}
            >
              <Settings className='fill-primary-default scale-80' />
            </span>
          </div>
        </div>
        <div className='flex max-md:flex-col'>
          <div className='relative max-md:flex md:w-72 md:pt-20'>
            {menuItems.map((item, i) => (
              <div
                key={i}
                className={`m-auto cursor-pointer py-5 max-md:mb-3 max-md:w-[33.33%] max-md:text-center max-md:text-sm md:pl-5 ${location.pathname.includes(item.link) ? activeTabClasses : 'text-[#90ABBD]'} `}
                onClick={() => navigate(item.link)}
              >
                {item.name}
              </div>
            ))}
            {/* Settings */}
            <div className='absolute bottom-6 flex w-full flex-col items-center justify-end gap-3 max-md:hidden'>
              <div
                className={`m-auto flex cursor-pointer gap-1 py-5 max-md:mb-3 max-md:w-[33.33%] max-md:text-center max-md:text-sm md:w-72 md:pt-20 md:pl-5 ${location.pathname.includes('settings') ? `${activeTabClasses} w-full! pt-5!` : 'text-[#90ABBD]'}`}
                onClick={() => navigate('/dashboard/settings')}
              >
                <Settings className={`scale-80 ${location.pathname.includes('settings') ? 'fill-blue-primary' : 'fill-[#90ABBD]'}`} />
                <span>Paramètres</span>
              </div>
              <div
                className='flex w-full cursor-pointer justify-center border-t pt-6'
                onClick={() => {
                  navigate(`/logout`);
                }}
              >
                <Icon.Logout className='mt-1 mr-3' />
                <span className='text-primary-default font-medium'>Se déconnecter</span>
              </div>
            </div>
          </div>
          <div
            className={`bg-white-light rounded-l-3xl p-10 ${isTabContentHeightShort && 'max-md:min-h-screen md:min-h-[calc(100vh-70px)]'} md:w-full`}
          >
            {location.pathname.includes('/dashboard/settings/') && (
              <div className='mb-8 flex cursor-pointer' onClick={() => navigate(getSettingsBreadcrumb(location.pathname).url)}>
                <Icon.ArrowCircleLeft className='mr-3' />
                <span className='text-[#7988A2]'>{getSettingsBreadcrumb(location.pathname).title}</span>
              </div>
            )}
            <Outlet />
          </div>
        </div>
      </div>
      <Seo title={'Dashboard'} />
    </>
  );
};

export default DashboardLayout;
